import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Play, RefreshCw } from "lucide-react";

/**
 *, Hidden engineering debug page for the simulation engine.
 *
 * Strictly internal: not linked from the customer nav, role-gated to
 * company_admin, and surfaces raw engine output for QA only. Will be
 * replaced by the proper Simulation UI in sub-phase 2F.
 */
function isoWeekStart(d = new Date()) {
  const x = new Date(d);
  const day = x.getDay();
  const diff = (day === 0 ? -6 : 1 - day);
  x.setDate(x.getDate() + diff);
  return x.toISOString().slice(0, 10);
}
function addDays(iso, n) {
  const x = new Date(iso);
  x.setDate(x.getDate() + n);
  return x.toISOString().slice(0, 10);
}

export default function AdminSimulationsPage() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [from, setFrom] = useState(isoWeekStart());
  const [to, setTo] = useState(addDays(isoWeekStart(), 6));
  const [running, setRunning] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/simulations?limit=25");
      setRuns(r.data.items || []);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Failed to load runs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post("/simulations/run",
        { from_date: from, to_date: to });
      toast.success(`Simulation ${r.data.simulation_id} · ${r.data.duration_ms}ms`);
      setSelected(r.data);
      refresh();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Run failed");
    } finally {
      setRunning(false);
    }
  };

  const openRun = async (sim_id) => {
    try {
      const r = await api.get(`/simulations/${sim_id}`);
      setSelected(r.data);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div data-testid="admin-simulations-page">
      <div className="mb-5">
        <div className="eyebrow flex items-center gap-2">
          Internal
          <span className="text-[10px] uppercase tracking-widest text-[#92400E] px-1.5 py-0.5 rounded bg-[#FEF3C7] border border-[#FDE68A]">
            Hidden, engineering only
          </span>
        </div>
        <h1 className="font-display text-3xl mt-1">Simulation engine, debug</h1>
        <p className="text-xs text-[#4B5773] mt-1 max-w-2xl">
           foundation. Runs the read-only simulation engine and shows the
          raw envelope. Not customer-facing; the proper UI ships in sub-phase 2F.
        </p>
      </div>

      <Card title="Run a new simulation">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <Label className="eyebrow">From</Label>
            <Input type="date" value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="h-10 mt-1" data-testid="sim-from" />
          </div>
          <div>
            <Label className="eyebrow">To</Label>
            <Input type="date" value={to}
              onChange={(e) => setTo(e.target.value)}
              className="h-10 mt-1" data-testid="sim-to" />
          </div>
          <Button onClick={runNow} disabled={running} data-testid="sim-run-btn"
            className="h-10 bg-[#2563EB] hover:bg-[#1D4ED8]">
            <Play className="h-4 w-4 mr-1" />{running ? "Running…" : "Run simulation"}
          </Button>
          <Button onClick={refresh} variant="outline" data-testid="sim-refresh-btn"
            className="h-10">
            <RefreshCw className="h-4 w-4 mr-1" />Refresh
          </Button>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-5">
        <Card title={`Recent runs (${runs.length})`}>
          {loading ? (
            <div className="text-sm text-[#4B5773]">Loading…</div>
          ) : runs.length === 0 ? (
            <div className="text-sm text-[#4B5773]">No runs yet. Click Run.</div>
          ) : (
            <ul className="space-y-2" data-testid="sim-runs-list">
              {runs.map((r) => (
                <li key={r.simulation_id}>
                  <button type="button" onClick={() => openRun(r.simulation_id)}
                    data-testid={`sim-run-${r.simulation_id}`}
                    className={`w-full text-left p-2 rounded-md border ${
                      selected?.simulation_id === r.simulation_id
                        ? "border-[#2563EB] bg-[#F0F6FF]"
                        : "border-slate-200 hover:border-[#2563EB]"
                    }`}>
                    <div className="text-xs font-mono text-[#0B1A36]">{r.simulation_id}</div>
                    <div className="text-[11px] text-[#4B5773]">
                      {r.status} · {r.duration_ms}ms ·
                      {" "}{r.result_summary?.proposals ?? 0} proposals ·
                      {" "}{r.result_summary?.gaps ?? 0} gaps
                    </div>
                    <div className="text-[10px] text-[#94A3B8] font-mono">
                      {r.scope?.from} → {r.scope?.to} · hash {r.inputs_snapshot_hash}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <div className="lg:col-span-2">
          <Card title={selected ? `Run ${selected.simulation_id}` : "Pick a run"}>
            {selected ? (
              <pre className="text-[11px] font-mono whitespace-pre-wrap break-words text-[#0B1A36] max-h-[600px] overflow-auto"
                data-testid="sim-detail-json">
{JSON.stringify(selected, null, 2)}
              </pre>
            ) : (
              <div className="text-sm text-[#4B5773]">Run a simulation, or pick one from the list.</div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
